import { useLocation } from 'react-router-dom';

/**
 * Tracks the pathname the user navigated FROM, so a screen can behave differently depending on
 * where the user arrived from. Used by the staff tables to decide whether their persisted
 * (sessionStorage-backed) filters should be restored or reset — see `staffTableSections.ts`.
 *
 * WHY THE PAIR OF VARIABLES (and why recording happens during render):
 *
 * The obvious implementation records the pathname in a `useEffect` at the app root and exposes
 * "the last recorded pathname", relying on React running child effects before parent effects: a
 * list component reading the value during its first render would still see the path it navigated
 * from, because the root's effect has not run yet.
 *
 * That reasoning does NOT hold in this app. Every staff list page is behind `React.lazy` (see
 * `Views/Frontend.tsx`), so on navigation the root commits a Suspense fallback FIRST — its effect
 * runs and records the new pathname — and the list component only renders once the chunk has
 * loaded. By then "the last recorded pathname" is the page you are already on, not the one you
 * came from, and every table would think it had arrived from inside its own section.
 *
 * So instead we keep an explicit `previous`/`current` pair and update it during the root's RENDER.
 * React always renders a parent before its children (suspended or not), which makes the ordering
 * unambiguous: by the time ANY descendant renders, `currentPathname` is the location being
 * rendered and `previousPathname` is the one before it. Consumers must therefore still read the
 * value during their first render (e.g. via a `useRef` latch) rather than from a mount effect,
 * but the value they read no longer depends on effect ordering at all.
 *
 * The write is a guarded, idempotent assignment to a module variable (a no-op when the pathname
 * has not changed), so React 18's StrictMode double-render and any re-render of the root are
 * harmless.
 */

/**
 * The current pathname is mirrored into sessionStorage so that a page refresh is treated as
 * "came from this same page" rather than "arrived from nowhere". Without this, F5 on a staff
 * table would silently wipe the filters the user was looking at, even though the filter stores
 * themselves survive the reload in sessionStorage.
 */
const LAST_PATHNAME_STORAGE_KEY = 'fh : navigation : last-pathname';

const readPersistedPathname = (): string | undefined => {
	try {
		return sessionStorage.getItem(LAST_PATHNAME_STORAGE_KEY) ?? undefined;
	} catch {
		// Private-mode / storage-disabled browsers: fall back to no history.
		return undefined;
	}
};

const persistPathname = (pathname: string): void => {
	try {
		sessionStorage.setItem(LAST_PATHNAME_STORAGE_KEY, pathname);
	} catch {
		// Non-fatal — the tracker just loses its across-refresh memory.
	}
};

let currentPathname: string | undefined;
let previousPathname: string | undefined = readPersistedPathname();

/**
 * Records a navigation. Exported for tests; app code should use `usePreviousLocationTracker`.
 */
export function recordPathname(pathname: string): void {
	if (pathname === currentPathname) {
		return;
	}
	// On the very first call of a page load `currentPathname` is undefined, so the value restored
	// from sessionStorage (the pathname we were on before the refresh) is kept as the previous one.
	if (currentPathname !== undefined) {
		previousPathname = currentPathname;
	}
	currentPathname = pathname;
	persistPathname(pathname);
}

/**
 * The pathname the user navigated from, or undefined when this is the first page of the session.
 * Must be read during render (see the note above), not from an effect.
 */
export function getPreviousPathname(): string | undefined {
	return previousPathname;
}

/**
 * Mount once, at the app root, inside the router (see `ObservabilityRoutes`).
 */
export function usePreviousLocationTracker(): void {
	const { pathname } = useLocation();
	// Deliberately during render — see the module comment.
	recordPathname(pathname);
}

/**
 * True when `pathname` is the section root itself or anything nested beneath it. Prefix matching
 * is segment-aware so `/bookings` never matches `/bookings-archive`.
 */
export function isPathInsideSection(pathname: string | undefined, sectionPrefixes: readonly string[]): boolean {
	if (pathname === undefined) {
		return false;
	}
	return sectionPrefixes.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * True when the previous route was inside one of `sectionPrefixes`.
 */
export function arrivedFromInsideSection(sectionPrefixes: readonly string[]): boolean {
	return isPathInsideSection(getPreviousPathname(), sectionPrefixes);
}
